import { AutoComplete, Spin } from 'antd';
import { NoResultsFound, SearchIcon } from 'components/forms/InstitutionSearch/components';
import { RECENT_PREFIX, getOptions } from 'components/forms/InstitutionSearch/utils';
import { useEffect, useMemo, useState } from 'react';

import { RoundedInput } from 'components/common/RoundedInput';
import { SearchIconContainer } from 'components/common/SearchIconContainer';
import { XIcon } from 'components/common/XIcon';
import useRecentSourceInstitutions from 'hooks/use-recent-source-institutions';
import useSelectedSourceInstitution from 'hooks/use-selected-source-institution';
import useSourceInstitutions from 'hooks/use-source-institutions';
import useTranslation from 'hooks/use-translation';

export default function InstitutionSearch(): JSX.Element {
  const translation = useTranslation();
  const sourceInstitutions = useSourceInstitutions();
  const { recentSourceInstitutions } = useRecentSourceInstitutions();
  const { selectedSourceInstitution, setSelectedSourceInstitution } = useSelectedSourceInstitution();

  const [searchTerm, setSearchTerm] = useState('');
  const [isPrefilled, setIsPrefilled] = useState(false);

  const options = useMemo(
    () => getOptions(searchTerm, recentSourceInstitutions, Array.from(sourceInstitutions.values())),
    [searchTerm, recentSourceInstitutions, sourceInstitutions],
  );

  // Handle initial pre-filling source institution & search results list from persistent store
  useEffect(() => {
    // if (isPrefilled) {
    //   return;
    // }
    const newSearchTerm = selectedSourceInstitution?.name ?? '';
    setSearchTerm(newSearchTerm);
    setIsPrefilled(true);
  }, [selectedSourceInstitution]);

  if (!translation) {
    return <Spin />;
  }

  const onSelect = (value: string) => {
    const isRecent = value.startsWith(RECENT_PREFIX);
    const selectedSourceInstitution = sourceInstitutions.get(isRecent ? value.split(RECENT_PREFIX)[1] : value);
    if (!selectedSourceInstitution) {
      return;
    }
    setSelectedSourceInstitution(selectedSourceInstitution.ID);
    setSearchTerm(selectedSourceInstitution.name);
  };

  const onSearch = async (value: string) => {
    if (value.length === 0) {
      setSelectedSourceInstitution(null);
    }
    setSearchTerm(value);
  };

  const onBlur = () => {
    setSearchTerm(selectedSourceInstitution?.name ?? '');
  };

  const onClear = () => {
    setSelectedSourceInstitution(null);
    setSearchTerm('');
  };

  const prefix = translation.common.icons.institutionSearchPrefix ? (
    <img
      src={`./config/icons/${translation.common.icons.institutionSearchPrefix.src}`}
      width={18}
      height={18}
      alt=''
      role='presentation'
    />
  ) : (
    <SearchIcon size={18} />
  );

  const suffix = searchTerm.length > 0 ? <XIcon size={24} onClick={onClear} /> : null;

  return (
    <AutoComplete
      options={options}
      notFoundContent={<NoResultsFound />}
      value={searchTerm}
      onBlur={onBlur}
      onSearch={onSearch}
      onSelect={onSelect}
      disabled={!isPrefilled}
    >
      <RoundedInput
        aria-label={options.length === 0 ? translation.home.institutionSearch.notFoundContent.heading : undefined}
        className='institution-search-container'
        placeholder={translation.home.institutionSearch.placeholder}
        prefix={<SearchIconContainer type='prefix'>{prefix}</SearchIconContainer>}
        suffix={<SearchIconContainer type='suffix'>{suffix}</SearchIconContainer>}
        title='Institution search'
      />
    </AutoComplete>
  );
}
